'use client';

import ArrowDropRight from 'icons/lib/arrow_drop_right.svg';
import Close from 'icons/lib/close_nav.svg';
import { useEffect, useState } from 'react';
import { Button, Sidebar } from 'ui';
import { formatCurrency, getCurrencyByLocale } from 'utils';

import { useTranslation } from '@/i18n/client';
import { fetchBrandsFilterList } from '@/modules/brands/api';
import {
  CheckboxFilter as CheckboxFilterType,
  ColorFilter as ColorFilterType,
  DiscountFilter as DiscountFilterType,
  fetchFilterList,
  RangeFilter as RangeFilterType,
} from '@/modules/product/api/productList';
import { useProductList } from '@/modules/product/context/productList';
import {
  renderDiscountFilterLabels,
  renderFilterLabels,
  renderPriceFilterLabels,
} from '@/modules/product/pages/productsListPage/utils';
import { OrderBy } from '@/shared/types/order-by';
import { useCurrentLocale } from '@/shared/utils/client';

import { CheckboxFilter } from '../checkboxFilter';
import { ColorFilter } from '../colorFilter';
import { DiscountFilter } from '../discountFilter';
import { formatFiltersBeforeSend } from '../filterBar/utils';
import {
  formatSidebarFilterList,
  resetSidebarFilterList,
} from '../filterBar/utils';
import { RangeFilter } from '../rangeFilter';
import { SortingOptions } from '../sortingOptions';
import styles from './FilterSidebar.module.scss';
import { FilterSidebarProps } from './types';

export function FilterSidebar({
  isOpen = false,
  selectedFilter,
  activeFilterList,
  sidebarFilters,
  sidebarFilteredFilterList,
  sidebarSort,
  sort,
  setActiveFilterList,
  setSidebarFilters,
  setSidebarFilteredFilterList,
  setSidebarSort,
  setSort,
  onClose,
}: FilterSidebarProps) {
  const locale = useCurrentLocale();
  const { t } = useTranslation(['products', 'common']);
  const {
    categorySlug,
    handleFetchFilters,
    filterList,
    filteredFilterList,
    filters,
    ascending,
    orderBy,
    brandSlug,
  } = useProductList();
  const [activeFilter, setActiveFilter] = useState<null | string>(null);

  function closeSidebar() {
    setTimeout(() => {
      setSidebarFilters(() =>
        formatSidebarFilterList(
          filterList,
          filters,
          formatCurrency(getCurrencyByLocale(locale))
        )
      );
      setSidebarFilteredFilterList(filteredFilterList);
      setSidebarSort(`${ascending}${orderBy}`);
    }, 200);

    onClose?.();
  }

  function changeActiveFilter(slug: string | null) {
    if (slug === null) {
      setActiveFilter(null);

      return;
    }

    setActiveFilter(slug);
  }

  function renderSortingLabel() {
    switch (sidebarSort) {
      case '1name':
        return t('productList.aToZ');
      case '0name':
        return t('productList.zToA');
      case '1price':
        return t('productList.leastExpensive');
      case '0price':
        return t('productList.mostExpensive');
      case '1updated_at':
        return t('productList.leastExpensive');
      case '0updated_at':
        return t('productList.leastExpensive');
      default:
        return t('productList.aToZ');
    }
  }

  function renderFilter() {
    if (activeFilter === 'sorter') {
      return (
        <SortingOptions
          sidebarSort={sidebarSort}
          setSidebarSort={setSidebarSort}
        />
      );
    }

    const filter = filterList.find((f) => f.slug === activeFilter);
    const filteredFilter = sidebarFilteredFilterList.find(
      (f) => f.slug === activeFilter
    );
    const selectedValues = sidebarFilters.find(
      (f) => f.slug === activeFilter
    )?.selectedValue;

    switch (filter?.filterType) {
      case 'range':
        return (
          <RangeFilter
            from={(filteredFilter as RangeFilterType)?.from ?? filter.from}
            slug={activeFilter ?? ''}
            sidebarFilters={sidebarFilters}
            selectedValues={selectedValues ? [...selectedValues] : []}
            to={(filteredFilter as RangeFilterType)?.to ?? filter.to}
            setSidebarFilters={setSidebarFilters}
            setSidebarFilteredFilterList={setSidebarFilteredFilterList}
            suffix={formatCurrency(getCurrencyByLocale(locale))}
          />
        );
      case 'color':
        return (
          <ColorFilter
            filteredValues={(filteredFilter as ColorFilterType)?.values ?? []}
            initialValues={filter.values}
            slug={activeFilter ?? ''}
            sidebarFilters={sidebarFilters}
            selectedValues={selectedValues ? [...selectedValues] : []}
            setSidebarFilters={setSidebarFilters}
            setSidebarFilteredFilterList={setSidebarFilteredFilterList}
          />
        );
      case 'checkbox':
        return (
          <CheckboxFilter
            filteredValues={
              (filteredFilter as CheckboxFilterType)?.values ?? []
            }
            initialValues={filter.values}
            unitName={filter.unit}
            slug={activeFilter ?? ''}
            sidebarFilters={sidebarFilters}
            selectedValues={selectedValues ? [...selectedValues] : []}
            setSidebarFilters={setSidebarFilters}
            setSidebarFilteredFilterList={setSidebarFilteredFilterList}
          />
        );
      case 'discount':
        return (
          <DiscountFilter
            filteredValues={
              (filteredFilter as DiscountFilterType)?.values ?? []
            }
            initialValues={filter.values}
            unitName={filter.unit}
            slug={activeFilter ?? ''}
            sidebarFilters={sidebarFilters}
            selectedValues={selectedValues ? [...selectedValues] : []}
            setSidebarFilters={setSidebarFilters}
            setSidebarFilteredFilterList={setSidebarFilteredFilterList}
          />
        );
    }
  }

  function handleResetFilters() {
    if (!activeFilter) {
      setActiveFilterList([]);
      categorySlug === 'search' ? setSort('1name') : setSort(`0sales_ratio`);
      categorySlug === 'search'
        ? handleFetchFilters(1, 'name', {})
        : handleFetchFilters(0, 'sales_ratio', {});

      setTimeout(() => {
        setSidebarFilters(() => resetSidebarFilterList(filterList));
        categorySlug === 'search'
          ? setSidebarSort(`1name`)
          : setSidebarSort(`0sales_ratio`);
        setSidebarFilteredFilterList(filteredFilterList);
      }, 200);

      onClose?.();
      return;
    }

    if (activeFilter === 'sorter') {
      const newFilters = formatFiltersBeforeSend(activeFilterList);

      categorySlug === 'search' ? setSort('1name') : setSort(`0sales_ratio`);
      categorySlug === 'search'
        ? handleFetchFilters(1, 'name', newFilters)
        : handleFetchFilters(0, 'sales_ratio', newFilters);

      setTimeout(() => {
        setSidebarSort(`0sales_ratio`);
        setSidebarFilters(() =>
          formatSidebarFilterList(
            filterList,
            filters,
            formatCurrency(getCurrencyByLocale(locale))
          )
        );
        setSidebarFilteredFilterList(filteredFilterList);
      }, 200);

      onClose?.();
      return;
    }

    const newSidebarFilterList = [...activeFilterList];

    const changedFilter = newSidebarFilterList.findIndex(
      (filter) => filter.slug === activeFilter
    );

    newSidebarFilterList.splice(changedFilter, 1);

    const newFilters = formatFiltersBeforeSend(newSidebarFilterList);

    setActiveFilterList(newSidebarFilterList);

    handleFetchFilters(
      Number(sort.charAt(0)),
      sort.substring(1) as OrderBy,
      newFilters
    );

    setTimeout(() => {
      setSidebarFilters(() => {
        const newValue = formatSidebarFilterList(
          filterList,
          filters,
          formatCurrency(getCurrencyByLocale(locale))
        );

        const sidebarFilterIndex = newValue.findIndex(
          (f) => f.slug === activeFilter
        );

        newValue[sidebarFilterIndex].selectedValue.length = 0;

        return newValue;
      });

      brandSlug
        ? fetchBrandsFilterList(brandSlug, newFilters).then((response) => {
            setSidebarFilteredFilterList(response);
          })
        : fetchFilterList(categorySlug, newFilters).then((response) => {
            setSidebarFilteredFilterList(response);
          });
    }, 200);

    onClose?.();
  }

  function handleSubmitFilters() {
    const newSidebarFilterList = [...activeFilterList];
    let newAscending = Number(sidebarSort.charAt(0));
    let newOrderBy = sidebarSort.substring(1) as
      | 'name'
      | 'price'
      | 'sales_ratio';

    sidebarFilters.forEach((sidebarFilter) => {
      const filterIndex = newSidebarFilterList.findIndex(
        (f) => f.slug === sidebarFilter.slug
      );

      if (sidebarFilter.selectedValue.length < 1) {
        if (filterIndex < 0) {
          return;
        }

        newSidebarFilterList.splice(filterIndex, 1);
        return;
      }

      if (filterIndex < 0) {
        newSidebarFilterList.push({ ...sidebarFilter });

        return;
      }

      newSidebarFilterList[filterIndex].selectedValue = [
        ...sidebarFilter.selectedValue,
      ];
    });

    const newFilters = formatFiltersBeforeSend(newSidebarFilterList);

    handleFetchFilters(newAscending, newOrderBy, newFilters);
    setActiveFilterList(newSidebarFilterList);
    setSort(`${newAscending}${newOrderBy}`);
    onClose?.();
  }

  useEffect(() => {
    if (isOpen) {
      changeActiveFilter(selectedFilter || null);
    } else {
      setTimeout(() => {
        setActiveFilter(null);
      }, 200);
    }
  }, [isOpen]);

  return (
    <Sidebar
      isOpen={isOpen}
      onOverlayPress={closeSidebar}
      wrapperClassName={styles.filtersSidebar}
    >
      <div className={styles.filtersSidebarHeader}>
        <div className={styles.filtersSidebarHeaderMain}>
          {activeFilter ? (
            <Button
              size="icon"
              variant="iconButton"
              onPress={() => changeActiveFilter(null)}
              buttonName="FilterSidebar__BackButton"
              startIcon={<ArrowDropRight />}
            >
              {filterList.find((f) => f.slug === activeFilter)?.name ||
                t('productList.sorting')}
            </Button>
          ) : (
            <p
              onClick={
                activeFilter ? () => changeActiveFilter(null) : undefined
              }
            >
              {t('productList.filters')}
            </p>
          )}
        </div>
        <Button
          size="icon"
          variant="iconButton"
          onPress={closeSidebar}
          buttonName="FilterSidebar__CloseButton"
        >
          <Close />
        </Button>
      </div>

      <div className={styles.filtersSidebarContent}>
        {!activeFilter ? (
          <ul className={styles.filtersSidebarAllFilters}>
            <li onClick={() => changeActiveFilter('sorter')}>
              <div>
                <span style={{ fontWeight: 'bold' }}>
                  {t('productList.sorting')}
                </span>
                <span>{renderSortingLabel()}</span>
              </div>
              <ArrowDropRight />
            </li>
            {sidebarFilters.map((filter, index) => {
              return (
                <li
                  key={`${filter.slug}_${index}`}
                  onClick={() => changeActiveFilter(filter.slug)}
                >
                  <div>
                    <span
                      style={{
                        fontWeight:
                          filter.selectedValue.length > 0 ? 'bold' : 'normal',
                      }}
                    >
                      {filter.name}
                    </span>
                    {filter.selectedValue.length > 0 && (
                      <span>
                        {filter.filterType === 'discount'
                          ? renderDiscountFilterLabels(
                              filter.selectedValue,
                              filter
                            )
                          : filter.slug === 'price'
                            ? renderPriceFilterLabels(
                                filter.selectedValue,
                                formatCurrency(getCurrencyByLocale(locale))
                              )
                            : renderFilterLabels(filter.selectedValue)}
                      </span>
                    )}
                  </div>
                  <ArrowDropRight />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles.filtersSidebarFiltersList}>
            {renderFilter()}
          </div>
        )}
      </div>

      <div className={styles.filtersSidebarActions}>
        <Button fullWidth variant="primary" onPress={handleResetFilters}>
          {t('productList.clearFilters')}
        </Button>
        <Button fullWidth variant="leaf" onPress={handleSubmitFilters}>
          {t('productList.submitFilters')}
        </Button>
      </div>
    </Sidebar>
  );
}
